import { WarningOutlined } from '@ant-design/icons';
import type { ModalFuncProps } from 'antd/es/modal';
import type { ButtonProps } from 'antd/lib/button';
import { Form, Modal } from 'antd';
import { ReactNode, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import styled, { ThemeContext } from 'styled-components';
import AppContext from '../components/modal/AppContext';
import DeleteModalContent from '../components/modal/DeleteModalContent';

const { confirm, info } = Modal;

const StyledWarningOutlined = styled(WarningOutlined)`
  font-size: 42px;
  color: #dc5034;
`;

type StringOrElementType = JSX.Element | string | ReactNode;
type ModalFunc = (props: ModalFuncProps) => {
  destroy: () => void;
  update: (newConfig: ModalFuncProps) => void;
};

export interface DeleteModalConfig {
  title: StringOrElementType;
  description: StringOrElementType;
  deleteItemNameTitle?: string;
  deleteItemNameValue?: string;
  extraInfo?: StringOrElementType;
  showDeleteInputField?: boolean;
  showWarning?: boolean;
  okText?: string;
  cancelText?: string;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  onOkAsync?: () => Promise<void> | undefined;
  onOk?: () => void;
  onCancel?: () => void;
  width?: number;
}

const useModal = () => {
  const intl = useIntl();
  const themeContext = useContext(ThemeContext);
  const [form] = Form.useForm();

  const showDeleteConfirm = useCallback(
    ({
      showWarning = true,
      title,
      deleteItemNameTitle,
      deleteItemNameValue,
      extraInfo,
      description,
      onOkAsync,
      onOk,
      onCancel,
      okText,
      cancelText,
      showDeleteInputField,
      okButtonProps,
      cancelButtonProps,
      width = 600,
    }: DeleteModalConfig) => {
      let modal: ReturnType<ModalFunc>;
      const handleHideConfirm = () => {
        modal.destroy();
      };
      const commonConfig: ModalFuncProps = {
        icon: null,
        width,
        maskClosable: false,
        keyboard: false,
        afterClose: () => {
          form.resetFields();
        },
        className: 'hide-confirm-btn delete-modal',
        title: (
          <AppContext intl={intl} themeContext={themeContext}>
            <StyledWarningOutlined type="WarningIcon" />
          </AppContext>
        ),
        content: (
          <DeleteModalContent
            intl={intl}
            themeContext={themeContext}
            title={title}
            deleteItemNameTitle={deleteItemNameTitle}
            deleteItemNameValue={deleteItemNameValue}
            extraInfo={extraInfo}
            description={description}
            form={form}
            showWarning={showWarning}
            showDeleteInputField={showDeleteInputField}
            okText={okText}
            cancelText={cancelText}
            onOk={onOk}
            onOkAsync={onOkAsync}
            onCancel={onCancel}
            okButtonProps={okButtonProps}
            cancelButtonProps={cancelButtonProps}
            handleHideConfirm={handleHideConfirm}
          />
        ),
        autoFocusButton: null,
      };
      if (showWarning) {
        modal = confirm({
          ...commonConfig,
        });

        return modal;
      }
      modal = info({
        ...commonConfig,
      });
      return modal;
    },
    [intl, themeContext, form],
  );

  return { showDeleteConfirm };
};

export { useModal };
